"use client";

import { useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { Disclaimer } from "@/components/disclaimer";
import { computeCompoundInterest } from "@/lib/calculators/compound-interest";
import { formatCompactCurrency, formatCurrency } from "@/lib/format";

function parseAmount(draft: string): number {
  const n = Number(draft.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-tabular text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

const SAVINGS_COLOR = "#6b7280";
const ETF_COLOR = "#16265C";
const STOCK_COLOR = "#C6A15B";

/**
 * Bildungs-Vergleichsrechner "Aktie vs. ETF vs. Tagesgeld" (backlog.md ->
 * Aktienfinder-Benchmark, "rein illustrativ") - ruft die bestehende, bereits getestete
 * computeCompoundInterest()-Funktion dreimal mit unterschiedlichen angenommenen Renditen
 * auf, kein neuer Berechnungscode. Bewusst KEINE Empfehlung, welche Anlageform "besser"
 * ist - alle drei Raten sind frei einstellbar, Startwerte sind grobe historische
 * Richtwerte, keine Prognose. Farben tragen bewusst keine Wertung (kein Grün/Rot),
 * gleiche neutrale Palette wie der Rest der App.
 */
export function AssetComparisonCalculator() {
  const [initialCapital, setInitialCapital] = useState("5000");
  const [monthlyContribution, setMonthlyContribution] = useState("200");
  const [years, setYears] = useState(20);
  const [savingsRate, setSavingsRate] = useState("2");
  const [etfRate, setEtfRate] = useState("7");
  const [stockRate, setStockRate] = useState("8");

  const savings = useMemo(
    () =>
      computeCompoundInterest({
        initialCapital: parseAmount(initialCapital),
        monthlyContribution: parseAmount(monthlyContribution),
        annualReturnRate: parseAmount(savingsRate) / 100,
        years,
      }),
    [initialCapital, monthlyContribution, savingsRate, years],
  );
  const etf = useMemo(
    () =>
      computeCompoundInterest({
        initialCapital: parseAmount(initialCapital),
        monthlyContribution: parseAmount(monthlyContribution),
        annualReturnRate: parseAmount(etfRate) / 100,
        years,
      }),
    [initialCapital, monthlyContribution, etfRate, years],
  );
  const stock = useMemo(
    () =>
      computeCompoundInterest({
        initialCapital: parseAmount(initialCapital),
        monthlyContribution: parseAmount(monthlyContribution),
        annualReturnRate: parseAmount(stockRate) / 100,
        years,
      }),
    [initialCapital, monthlyContribution, stockRate, years],
  );

  const chartData = useMemo(() => {
    if (!savings || !etf || !stock) return [];
    return savings.points.map((point, i) => ({
      year: point.year,
      savings: point.balance,
      etf: etf.points[i]?.balance,
      stock: stock.points[i]?.balance,
    }));
  }, [savings, etf, stock]);

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline p-4">
      <div>
        <span className="font-serif text-lg font-semibold text-ink">Aktie vs. ETF vs. Tagesgeld</span>
        <p className="mt-1 text-xs text-ink-secondary">
          Rein hypothetische Modellrechnung mit drei frei einstellbaren, konstant
          angenommenen Renditen - zeigt nur den mathematischen Zinseszins-Effekt
          unterschiedlicher Annahmen, keine Prognose und keine Einordnung, welche
          Anlageform für dich passend ist.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Startkapital (€)
          <Input
            type="number"
            min={0}
            value={initialCapital}
            onChange={(e) => setInitialCapital(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Sparrate/Monat (€)
          <Input
            type="number"
            min={0}
            value={monthlyContribution}
            onChange={(e) => setMonthlyContribution(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Anlagedauer (Jahre)
          <select
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            className="rounded-md border border-hairline bg-card px-2 py-1.5 text-sm text-ink"
          >
            {[5, 10, 15, 20, 25, 30, 35, 40].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: SAVINGS_COLOR }} />
            Tagesgeld (%/Jahr)
          </span>
          <Input
            type="number"
            value={savingsRate}
            onChange={(e) => setSavingsRate(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: ETF_COLOR }} />
            ETF/breiter Markt (%/Jahr)
          </span>
          <Input type="number" value={etfRate} onChange={(e) => setEtfRate(e.target.value)} className="bg-card" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: STOCK_COLOR }} />
            Einzelaktie (%/Jahr)
          </span>
          <Input
            type="number"
            value={stockRate}
            onChange={(e) => setStockRate(e.target.value)}
            className="bg-card"
          />
        </label>
      </div>

      {savings && etf && stock && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile label="Tagesgeld: Endkapital" value={formatCurrency(savings.finalBalance, "EUR")} />
            <StatTile label="ETF: Endkapital" value={formatCurrency(etf.finalBalance, "EUR")} />
            <StatTile label="Einzelaktie: Endkapital" value={formatCurrency(stock.finalBalance, "EUR")} />
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#16265C" strokeOpacity={0.14} strokeDasharray="3 5" vertical={false} />
                <XAxis
                  dataKey="year"
                  tickFormatter={(y) => `${y}J`}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, "auto"]}
                  tickFormatter={(v) => formatCompactCurrency(v, "EUR")}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <Tooltip
                  formatter={(value, name) => [
                    formatCurrency(Number(value), "EUR"),
                    name === "savings" ? "Tagesgeld" : name === "etf" ? "ETF" : "Einzelaktie",
                  ]}
                  labelFormatter={(y) => `Jahr ${y}`}
                />
                <Line type="linear" dataKey="savings" stroke={SAVINGS_COLOR} strokeWidth={2} dot={false} />
                <Line type="linear" dataKey="etf" stroke={ETF_COLOR} strokeWidth={2} dot={false} />
                <Line type="linear" dataKey="stock" stroke={STOCK_COLOR} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      <p className="text-[0.65rem] text-muted-foreground">
        Alle drei Linien nutzen eine konstante, frei wählbare Durchschnittsrendite - reale
        Kursverläufe schwanken stark, besonders bei Einzelaktien (Klumpenrisiko, keine
        Diversifikation). Ohne Steuern/Gebühren. Keine Anlageberatung, keine Aussage
        darüber, welche Anlageform für dich geeignet ist.
      </p>
      <Disclaimer />
    </div>
  );
}
